import React, { useState, useMemo } from 'react';
import { CATALOG_DATA, CatalogProduct } from '../data/catalog';
import { ProductDrawer } from './ProductDrawer';
import { TrackDialog } from './TrackDialog';
import { PreorderResult, Region, SearchPreordersRequest, TrackProductInput } from '../types';
import { addTrackedProduct } from '../utils/api';

interface Props {
  onSearch: (req: SearchPreordersRequest) => void;
}

const LINE_COLORS: Record<string, string> = {
  fusion_world: '#f97316',
  masters: '#8b5cf6',
};

const LINE_LABELS: Record<string, string> = {
  fusion_world: 'Fusion World',
  masters: 'Masters',
};

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}

function countdown(days: number): { label: string; color: string; urgent: boolean } {
  if (days <= 0) return { label: 'Released', color: '#22c55e', urgent: false };
  if (days === 1) return { label: 'Tomorrow!', color: '#ef4444', urgent: true };
  if (days <= 7) return { label: `${days}d`, color: '#ef4444', urgent: true };
  if (days <= 30) return { label: `${days}d`, color: '#f97316', urgent: false };
  if (days <= 90) return { label: `${days}d`, color: '#eab308', urgent: false };
  return { label: `${days}d`, color: '#3b82f6', urgent: false };
}

function quarterLabel(dateStr: string): string {
  const d = new Date(dateStr);
  return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Construct a minimal PreorderResult shape so TrackDialog can be reused
function toPreorderResult(p: CatalogProduct): PreorderResult {
  return {
    productId: p.id,
    productTitle: p.title,
    productType: p.line,
    releaseDate: p.releaseDate,
    releaseDateStatus: p.releaseDateStatus,
    storeName: '',
    storeUrl: '',
    region: 'EU' satisfies Region,
    currency: 'EUR',
    price: null,
    stockStatus: p.releaseDate && new Date(p.releaseDate) > new Date() ? 'Coming Soon' : 'Not Yet Listed',
    lastCheckedAt: new Date().toISOString(),
  };
}

export function UpcomingPanel({ onSearch }: Props) {
  const [lineFilter, setLineFilter] = useState<'all' | 'fusion_world' | 'masters'>('all');
  const [drawerProduct, setDrawerProduct] = useState<CatalogProduct | null>(null);
  const [trackTarget, setTrackTarget] = useState<CatalogProduct | null>(null);
  const [toast, setToast] = useState('');

  const upcoming = useMemo(() => {
    const now = new Date();
    return CATALOG_DATA
      .filter((p) => {
        if (lineFilter !== 'all' && p.line !== lineFilter) return false;
        if (p.releaseDate && new Date(p.releaseDate) <= now) return false;
        return true;
      })
      .sort((a, b) => {
        if (!a.releaseDate && !b.releaseDate) return 0;
        if (!a.releaseDate) return 1;
        if (!b.releaseDate) return -1;
        return new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
      });
  }, [lineFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, CatalogProduct[]>();
    for (const p of upcoming) {
      const key = p.releaseDate ? quarterLabel(p.releaseDate) : 'TBA';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return map;
  }, [upcoming]);

  function handleSearch(p: CatalogProduct) {
    onSearch({
      productType: p.line as 'fusion_world' | 'masters',
      query: p.setCode,
      regions: ['ES', 'EU', 'UK', 'US'],
      newReleasesOnly: false,
    });
  }

  async function handleTrackConfirm(input: TrackProductInput) {
    try {
      await addTrackedProduct(input);
      setTrackTarget(null);
      setToast(`"${input.productTitle}" added to tracked products.`);
      setTimeout(() => setToast(''), 4000);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Failed to track.');
      setTimeout(() => setToast(''), 4000);
    }
  }

  const nextUp = upcoming.find((p) => p.releaseDate);

  return (
    <div style={s.container}>
      {toast && <div style={s.toast}>{toast}</div>}

      {/* Next release highlight */}
      {nextUp && nextUp.releaseDate && (
        <div style={{ ...s.nextRelease, borderColor: LINE_COLORS[nextUp.line] + '60' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#8a93b0', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                Next release
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <code style={{ fontWeight: 800, color: LINE_COLORS[nextUp.line], fontFamily: 'monospace', fontSize: '14px' }}>
                  {nextUp.setCode}
                </code>
                <span style={{ fontSize: '18px', fontWeight: 800, color: '#e8eaf0' }}>{nextUp.title}</span>
                {(() => {
                  const c = countdown(daysUntil(nextUp.releaseDate!));
                  return (
                    <span style={{ fontSize: '13px', fontWeight: 800, color: c.color, background: c.color + '20', padding: '3px 10px', borderRadius: '20px' }}>
                      {c.label}
                    </span>
                  );
                })()}
              </div>
              <div style={{ fontSize: '13px', color: '#8a93b0', marginTop: '4px' }}>
                {formatDate(nextUp.releaseDate!)} · {LINE_LABELS[nextUp.line]} · {nextUp.productType}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button style={s.searchBtn} onClick={() => handleSearch(nextUp)}>Search stores</button>
              <button style={s.storesBtn} onClick={() => setDrawerProduct(nextUp)}>View preorder links</button>
            </div>
          </div>
        </div>
      )}

      {/* Line filter */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {(['all', 'fusion_world', 'masters'] as const).map((v) => (
          <button
            key={v}
            style={{ ...s.filterBtn, ...(lineFilter === v ? s.filterBtnActive : {}) }}
            onClick={() => setLineFilter(v)}
          >
            {v === 'all' ? 'All lines' : LINE_LABELS[v]}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#8a93b0' }}>
          {upcoming.length} upcoming release{upcoming.length !== 1 ? 's' : ''}
        </span>
      </div>

      {upcoming.length === 0 && (
        <div style={s.empty}>No upcoming releases match the current filter.</div>
      )}

      {/* Quarter groups */}
      {Array.from(grouped.entries()).map(([quarter, items]) => (
        <div key={quarter}>
          <div style={s.quarterDivider}>
            <span style={s.quarterLabel}>{quarter}</span>
            <div style={s.dividerLine} />
            <span style={s.quarterCount}>{items.length} release{items.length !== 1 ? 's' : ''}</span>
          </div>

          <div style={s.grid}>
            {items.map((p) => {
              const days = p.releaseDate ? daysUntil(p.releaseDate) : null;
              const cd = days !== null ? countdown(days) : null;
              const lineColor = LINE_COLORS[p.line];

              return (
                <div key={p.id} style={{ ...s.card, borderColor: cd?.urgent ? lineColor + '80' : '#2e3347' }}>
                  {/* Card top row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span style={{ ...s.lineDot, background: lineColor }} />
                      <span style={{ fontSize: '11px', fontWeight: 700, color: lineColor }}>
                        {LINE_LABELS[p.line]}
                      </span>
                    </div>
                    {cd ? (
                      <span style={{ fontSize: '11px', fontWeight: 800, color: cd.color, background: cd.color + '20', padding: '2px 8px', borderRadius: '20px' }}>
                        {cd.label}
                      </span>
                    ) : (
                      <span style={{ fontSize: '11px', color: '#6b7280', background: '#6b728020', padding: '2px 8px', borderRadius: '20px' }}>TBA</span>
                    )}
                  </div>

                  {/* Set code + title */}
                  <div>
                    <code style={{ fontSize: '12px', fontWeight: 800, color: lineColor, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
                      {p.setCode}
                    </code>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#e8eaf0', lineHeight: 1.25, marginTop: '3px' }}>
                      {p.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8a93b0', marginTop: '3px' }}>{p.series}</div>
                  </div>

                  {/* Meta row */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={s.typePill}>{p.productType}</span>
                    {p.releaseDate && (
                      <span style={{ fontSize: '12px', color: '#8a93b0' }}>
                        {formatDate(p.releaseDate)}
                        {p.releaseDateStatus === 'Unconfirmed' && (
                          <span style={{ color: '#eab308', marginLeft: '3px' }}>·?</span>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Price */}
                  {p.msrpPerPack && (
                    <div style={{ fontSize: '13px' }}>
                      <span style={{ color: '#f97316', fontWeight: 700 }}>
                        ~${(p.msrpPerPack * p.packsPerBox).toFixed(0)} USD
                      </span>
                      <span style={{ color: '#8a93b0', marginLeft: '6px' }}>
                        ({p.packsPerBox} packs · ${p.msrpPerPack}/pack)
                      </span>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div style={s.cardActions}>
                    <button style={s.actionPrimary} onClick={() => setDrawerProduct(p)}>
                      Stores &amp; details
                    </button>
                    <button style={s.actionSecondary} onClick={() => handleSearch(p)}>
                      Search stores
                    </button>
                    <button style={s.actionGhost} onClick={() => setTrackTarget(p)}>
                      + Track
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <ProductDrawer product={drawerProduct} onClose={() => setDrawerProduct(null)} />

      {trackTarget && (
        <TrackDialog
          product={toPreorderResult(trackTarget)}
          onConfirm={handleTrackConfirm}
          onClose={() => setTrackTarget(null)}
        />
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '24px' },
  toast: {
    padding: '12px 16px', background: '#22c55e20', border: '1px solid #22c55e',
    borderRadius: '8px', color: '#22c55e', fontSize: '13px',
  },
  nextRelease: {
    background: 'linear-gradient(135deg, #1a1d27 0%, #1e1530 100%)',
    border: '1.5px solid', borderRadius: '14px', padding: '20px 24px',
  },
  filterBtn: {
    padding: '7px 16px', border: '1.5px solid #2e3347', borderRadius: '20px',
    background: '#1a1d27', color: '#8a93b0', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
  },
  filterBtnActive: { borderColor: '#f97316', background: '#f9731620', color: '#f97316' },
  empty: {
    padding: '48px', textAlign: 'center', color: '#8a93b0', fontSize: '14px',
    background: '#1a1d27', borderRadius: '12px', border: '1.5px solid #2e3347',
  },
  quarterDivider: {
    display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px',
  },
  quarterLabel: { fontSize: '13px', fontWeight: 700, color: '#e8eaf0', whiteSpace: 'nowrap' },
  dividerLine: { flex: 1, height: '1px', background: '#2e3347' },
  quarterCount: { fontSize: '12px', color: '#8a93b0', whiteSpace: 'nowrap' },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px',
    marginBottom: '24px',
  },
  card: {
    background: '#1a1d27', border: '1.5px solid #2e3347', borderRadius: '12px',
    padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  lineDot: { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  typePill: {
    fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px',
    background: '#2e3347', color: '#8a93b0',
  },
  cardActions: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' },
  actionPrimary: {
    flex: '1 1 auto', padding: '8px 12px', background: '#f97316', border: 'none',
    borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
  },
  actionSecondary: {
    flex: '1 1 auto', padding: '8px 12px', background: '#f9731615', border: '1px solid #f9731650',
    borderRadius: '8px', color: '#f97316', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
  },
  actionGhost: {
    padding: '8px 12px', background: 'transparent', border: '1px solid #2e3347',
    borderRadius: '8px', color: '#8a93b0', cursor: 'pointer', fontSize: '12px',
  },
  searchBtn: {
    padding: '9px 18px', background: '#f9731615', border: '1px solid #f9731650',
    borderRadius: '8px', color: '#f97316', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
  },
  storesBtn: {
    padding: '9px 18px', background: '#f97316', border: 'none',
    borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 700,
  },
};
